'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import { Button } from '@/components/ui/button'
import getUserName from '@/helpers/get-user-name'
import { getUserProfilePictureUrl } from '@/helpers/user'
import AnalyticsConfig from '@/resources/analytics.json'
import apiGet from '@/store/helpers/api-get'
import { ExternalLinkIcon } from 'lucide-react'
import { signIn } from 'next-auth/react'
import Image from 'next/image'
import { useState } from 'react'
import useSWR from 'swr'
import DriverParticipationChart from '../charts/driverParticipation'
import LapsLoggedChart from '../charts/lapsLogged'
import LapsPerDriverChart from '../charts/lapsPerDriver'
import PointDistributrionChart from '../charts/pointDistribution'
import StandingsTable from '../charts/standings'
import RoundSelector from './roundSelector'
import BarChartSkeleton from './skeleton'

export default function Analytics({ seasonId, viewer }) {
  const [round, setRound] = useState('All Rounds')

  const { data: data, isLoading } = useSWR(
    {
      url: '/analytics/all',
      query: { seasonId: seasonId, isVirtual: 'False' },
    },
    apiGet,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  const analyticsConfig = AnalyticsConfig.find(
    (configItem) => configItem.seasonId === seasonId,
  )?.global

  return (
    <div className="screen-grid gap-y-4 *:rounded desktop-sm:gap-y-5 desktop-md:gap-y-8">
      <div className="relative col-span-full min-h-[200px] overflow-hidden tablet:col-span-6 desktop-sm:col-span-9">
        <Image
          fill
          src={analyticsConfig.header.backgroundImgPath}
          className="z-0 object-cover object-bottom"
          alt="Activations header image"
        />
        <div className="absolute z-10 flex h-full w-full flex-col items-center justify-center bg-shades-100/70">
          <div className="relative aspect-[3.66666] h-8">
            <Image
              fill
              src={analyticsConfig.header.logoImgPath}
              className=""
              alt="Analytics logo image"
            />
          </div>
          <p className="mb-3 mt-9 text-p-2 font-semibold text-primary">
            {analyticsConfig.header.name}
          </p>
          <h2 className="text-center font-syncopate text-h-3 font-bold text-shades-0 desktop-sm:text-d-2">
            {analyticsConfig.header.seasonName} ANALYSIS
          </h2>
        </div>
      </div>

      <div className="bg-gf-gradient relative col-span-full aspect-[0.91] tablet:col-span-2 desktop-sm:col-span-3">
        <div className="absolute flex h-full w-full flex-col items-center">
          <div className="my-auto flex flex-col items-center">
            <div className="relative size-20 overflow-hidden rounded-full tablet:size-6 desktop-xs:size-10 desktop-sm:size-20">
              <ImageWithFallback
                src={getUserProfilePictureUrl(viewer, {
                  profile: 'driver',
                })}
                fill
                alt="The current users profile pic"
              />
            </div>
            {viewer && (
              <p className="mt-1 text-p-1 font-semibold text-shades-0/80 desktop-xs:text-p-3">
                {getUserName({ user: viewer }, { profile: 'driver' })}
              </p>
            )}
            <h5 className="mt-2 text-center text-p-3 font-semibold text-shades-0 desktop-xs:mt-4 desktop-xs:text-h-5">
              See my personal analysis
            </h5>
            {viewer ? (
              <Button
                className="mt-5 bg-shades-0 px-5 py-3 text-p-3 font-semibold text-neutral-blue-900"
                onClick={() =>
                  window.open(
                    window.location.href.concat(`/${viewer.id}`),
                    '_blank',
                  )
                }
              >
                Take a look
                <ExternalLinkIcon className="ml-2 size-6 stroke-neutral-blue-900 stroke-2" />
              </Button>
            ) : (
              <Button
                className="mt-5 bg-shades-0 px-5 py-3 text-p-3 font-semibold text-neutral-blue-900"
                onClick={signIn}
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>

      {analyticsConfig.ctaBanner.active && (
        <>
          <div className="divide col-span-full bg-neutral-blue-700" />
          <div className="col-span-full aspect-[8.95] max-tablet:aspect-[7]">
            <div className="relative col-span-full h-full w-full">
              <Image
                fill
                src={analyticsConfig.ctaBanner.backgroundImgPath}
                className="z-0"
                alt="Analytics call to action banner image"
              />
              <div className="absolute z-10 flex h-full w-full items-center justify-center gap-5">
                <h5 className="text-center text-footer font-bold text-shades-0 tablet:text-cap desktop-xs:text-h-5">
                  {analyticsConfig.ctaBanner.title}
                </h5>
                <Button
                  className="px-2 py-1 desktop-xs:px-5 desktop-xs:py-3"
                  onClick={() => window.open(analyticsConfig.ctaBanner.link)}
                >
                  <p className="text-semibold mobile:text-footer tablet:text-p-1 desktop-xs:text-p-3">
                    {analyticsConfig.ctaBanner.buttonText}
                  </p>
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="divide col-span-full bg-neutral-blue-700"></div>

      <div className="flex items-center gap-3.5 desktop-xs:-my-2">
        <p className="whitespace-nowrap text-p-2 text-neutral-blue-500">
          Filter all
        </p>
        <RoundSelector round={round} setRound={setRound} />
      </div>

      <div className="divide col-span-full bg-neutral-blue-700"></div>

      <div className="col-span-full h-full border border-neutral-blue-700 bg-neutral-blue-800 p-4 tablet:col-span-4 tablet:p-6 desktop-sm:col-span-6 desktop-md:p-8 desktop-lg:p-12">
        {data && !isLoading && (
          <DriverParticipationChart
            round={round}
            seasonId={seasonId}
            overallData={data}
          />
        )}
        {isLoading && <BarChartSkeleton />}
      </div>

      <div className="col-span-full flex h-full flex-col gap-4 border border-neutral-blue-700 bg-neutral-blue-800 p-4 tablet:col-span-4 tablet:p-6 desktop-sm:col-span-6 desktop-md:p-8 desktop-lg:p-12">
        {data && !isLoading && (
          <LapsLoggedChart
            round={round}
            seasonId={seasonId}
            overallData={data}
          />
        )}
        {isLoading && <BarChartSkeleton />}
      </div>

      <div className="col-span-full flex flex-col border border-neutral-blue-700 bg-neutral-blue-800 p-4 tablet:col-span-4 tablet:p-6 desktop-sm:col-span-6 desktop-md:p-8 desktop-lg:p-12">
        {data && !isLoading && (
          <LapsPerDriverChart
            round={round}
            seasonId={seasonId}
            overallData={data}
          />
        )}
        {isLoading && <BarChartSkeleton />}
      </div>
      <div className="col-span-full flex flex-col border border-neutral-blue-700 bg-neutral-blue-800 p-4 tablet:col-span-4 tablet:p-6 desktop-sm:col-span-6 desktop-md:p-8 desktop-lg:p-12">
        {data && !isLoading && (
          <PointDistributrionChart
            round={round}
            seasonId={seasonId}
            overallData={data}
          />
        )}
        {isLoading && <BarChartSkeleton />}
      </div>
      <div className="col-span-full border border-neutral-blue-700 bg-neutral-blue-800">
        {data && !isLoading && <StandingsTable standings={data} />}
      </div>
    </div>
  )
}
